import { useCallback, useState } from "react";

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/**
 * 基于 localStorage 的缓存状态 hook，用法与 `useState` 类似。
 *
 * - 初始值优先从 `storageKey` 对应的本地缓存中读取，读取失败或不存在时回退到 `defaultValue`；
 * - 默认值为普通对象时，会与缓存值做浅合并，便于在新增字段后仍兼容旧缓存；
 * - `storageKey` 为空字符串时退化为内存状态，不读不写本地缓存。
 *
 * @param storageKey localStorage 缓存键；为空字符串时不持久化
 * @param defaultValue 默认值（也用于浅合并模板）
 * @returns `[state, setState]` 元组，调用 setState 会同步写入本地缓存
 */
export function useCachedState<T>(
  storageKey: string,
  defaultValue: T,
): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    if (!storageKey) return defaultValue;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw == null) return defaultValue;
      const parsed = JSON.parse(raw);
      return isPlainObject(defaultValue) && isPlainObject(parsed)
        ? ({ ...defaultValue, ...parsed } as T)
        : (parsed as T);
    } catch {
      return defaultValue;
    }
  });

  const setCachedState = useCallback(
    (value: T) => {
      setState(value);
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(value));
      } catch (err) {
        console.error("useCachedState error", storageKey, err);
      }
    },
    [storageKey],
  );

  return [state, setCachedState];
}
